// ────────────────────────────────────────────────────────────────
// src/pages/SchedulePaymentPage.js
// Create or edit a recurring payment schedule
//
// • Layout restyled to match SocialPage card model.
// • Added wallet-PM validation: rejects Bancontact/Sofort/etc. tokens
//   that can’t be charged in ¥ off-session.
// • 2025-07-28 bug-fix: use stripe.paymentMethods.retrieve()
//   (stripe.retrievePaymentMethod does not exist).
// • ALL previous logic preserved.
// ────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useRef } from 'react'
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import axios from 'axios'

function SchedulePaymentPage() {
  /* ───────────────────────────────
     React-Router & Stripe hooks
  ─────────────────────────────── */
  const nav        = useNavigate()
  const { id }     = useParams()           // edit-mode if id exists
  const stripe     = useStripe()
  const elements   = useElements()

  /* ───────────────────────────────
     Context & Auth
  ─────────────────────────────── */
  const { lang, openInviteModal } = useOutletContext() || {}
  const token  = localStorage.getItem('token')
  const user   = JSON.parse(localStorage.getItem('user')) || {}

  /* ───────────────────────────────
     i18n
  ─────────────────────────────── */
  const i18n = {
    en:{ titleNew:'Schedule Payment', titleEdit:'Edit Schedule',
         to:'Recipient E-mail', amt:'Amount (¥, min 50)',
         freq:'Frequency', start:'Start Date', pm:'Payment Method',
         weekly:'Weekly', monthly:'Monthly', yearly:'Yearly',
         wallet:'Apple / Google Pay', cc:'Credit Card (Visa/Mastercard)',
         jcb:'Credit Card (JCB)', bank:'Bank Transfer (Furikomi)',
         enterCardDetails:'Enter Card Details',
         noPaySupport:'Your device does not support Apple / Google Pay.',
         walletBad:'Your wallet returned a payment method that cannot '
                   + 'be charged in ¥ off-session. '
                   + 'Please choose a Visa / Mastercard card.',
         save:'Save', cancel:'Cancel', success:'✅ Schedule saved!',
         sending:'Saving…', errSmall:'Amount must be ≥ 50',
         errGen:'Server error',
         invalidPm:'Please provide a valid payment method.',
         bankTitle:'Bank-Transfer Instructions',
         furikomiDetails:
           'Use the information below to complete your furikomi:',
         okay:'OK', inviteFriendLabel:'Invite your friend' },
    ja:{ titleNew:'定期支払いを作成', titleEdit:'スケジュールを編集',
         to:'受取人メール', amt:'金額 (¥ 最低50)',
         freq:'頻度', start:'開始日', pm:'支払い方法',
         weekly:'毎週', monthly:'毎月', yearly:'毎年',
         wallet:'Apple / Google Pay', cc:'クレジットカード (Visa/Mastercard)',
         jcb:'クレジットカード (JCB)', bank:'銀行振込 (振込)',
         enterCardDetails:'カード情報を入力',
         noPaySupport:'このデバイスは Apple / Google Pay に対応していません。',
         walletBad:'このウォレットは ¥ で課金できない決済方法を返しました。'
                   + 'Visa / Mastercard を選択してください。',
         save:'保存', cancel:'キャンセル', success:'✅ 保存しました！',
         sending:'保存中…', errSmall:'最低金額は50円です', errGen:'サーバーエラー',
         invalidPm:'有効な決済方法を入力してください。',
         bankTitle:'振込情報',
         furikomiDetails:'以下の情報で振込を行ってください:',
         okay:'OK', inviteFriendLabel:'友達を招待' },
    pt:{ titleNew:'Agendar Pagamento', titleEdit:'Editar Agendamento',
         to:'E-mail do destinatário', amt:'Valor (¥, mín 50)',
         freq:'Frequência', start:'Data inicial', pm:'Método de pagamento',
         weekly:'Semanal', monthly:'Mensal', yearly:'Anual',
         wallet:'Apple / Google Pay', cc:'Cartão de Crédito (Visa/Mastercard)',
         jcb:'Cartão de Crédito (JCB)', bank:'Transferência Bancária (Furikomi)',
         enterCardDetails:'Insira os dados do cartão',
         noPaySupport:'Este dispositivo não suporta Apple / Google Pay.',
         walletBad:'A carteira retornou um método que não pode ser cobrado '
                   + 'em ienes off-session. Escolha um cartão Visa / Mastercard.',
         save:'Salvar', cancel:'Cancelar', success:'✅ Agendamento salvo!',
         sending:'Salvando…', errSmall:'Valor mínimo ¥50', errGen:'Erro no servidor',
         invalidPm:'Insira um método de pagamento válido.',
         bankTitle:'Instruções para Furikomi',
         furikomiDetails:'Use as informações abaixo para concluir a transferência:',
         okay:'OK', inviteFriendLabel:'Convide seu amigo' },
  }
  const t = i18n[lang] || i18n.en

  /* ───────────────────────────────
     Component State
  ─────────────────────────────── */
  const [form,setForm]=useState({
    toUserEmail:'', amount:'', freq:'monthly',
    startDate:new Date().toISOString().slice(0,10),
    paymentMethodType:'wallet',
  })
  const [amountWarning,setAmountWarning]   = useState('')
  const [loading,setLoading]               = useState(!!id)
  const [saving,setSaving]                 = useState(false)
  const [msg,setMsg]                       = useState(null)

  /* email-autocomplete */
  const [emailSearchTerm,setEmailSearchTerm] = useState('')
  const [suggestions,setSuggestions]         = useState([])
  const [showSuggestions,setShowSuggestions] = useState(false)

  /* Stripe – Payment Request (wallet) */
  const [paymentRequest,setPaymentRequest]   = useState(null)
  const [canUsePaymentRequest,setCanUsePaymentRequest] = useState(false)
  const paymentRequestRef = useRef(null)

  /* bank-transfer modal */
  const [bankInstructions,setBankInstructions] = useState(null)
  const [showBankModal,setShowBankModal]       = useState(false)

  /* error modal */
  const [errorMsg,setErrorMsg] = useState(null)

  /* ───────────────────────────────
     Fetch schedule when editing
  ─────────────────────────────── */
  useEffect(()=>{
    const run=async()=>{
      if(!id) return
      try{
        const { data } = await axios.get(
          'https://payme-backend-713827320947.asia-northeast1.run.app/api/schedule/list',
          { headers:{ Authorization:`Bearer ${token}` }})
        const sched=data.find(s=>String(s.id)===id)
        if(!sched){ nav('/scheduled-payments'); return }
        setForm({
          toUserEmail:sched.to_user_email||'',
          amount:String(sched.amount),
          freq:sched.freq,
          startDate:sched.start_date,
          paymentMethodType:sched.payment_method_type||'wallet',
        })
      }catch(e){ console.error(e); setErrorMsg(t.errGen) }
      setLoading(false)
    }
    run()
  },[id,token,nav,t.errGen])

  /* ───────────────────────────────
     Email autocomplete
  ─────────────────────────────── */
  useEffect(()=>{
    if(emailSearchTerm.length<2){ setSuggestions([]); return }
    const run=async()=>{
      try{
        const { data } = await axios.get(
          `https://payme-backend-713827320947.asia-northeast1.run.app/api/users/search?query=${encodeURIComponent(emailSearchTerm)}`,
          { headers:{ Authorization:`Bearer ${token}` }})
        setSuggestions(data.length?data:[{id:'inviteFriend'}])
      }catch(e){ console.error(e); setSuggestions([]) }
    }
    run()
  },[emailSearchTerm,token])

  const formatSuggestion = it =>
    it.id==='inviteFriend'
      ? t.inviteFriendLabel
      : it.name
        ? `${it.name} - ${it.email}`
        : it.email

  /* ───────────────────────────────
     Payment Request (wallet)
  ─────────────────────────────── */
  useEffect(()=>{
    if(!stripe) return

    const digits=parseInt(form.amount.replace(/\D/g,''),10)||0
    const ready=
      form.paymentMethodType==='wallet' &&
      form.toUserEmail.trim()!=='' &&
      digits>=50

    if(!ready){
      setPaymentRequest(null); setCanUsePaymentRequest(false); return
    }

    const pr=stripe.paymentRequest({
      country:'JP',
      currency:'jpy',
      total:{ label:'PayMe Scheduled Payment', amount:digits },
      requestPayerName:true,
      requestPayerEmail:true,
    })
    pr.canMakePayment().then(r=>
      setCanUsePaymentRequest(!!r && (r.applePay||r.googlePay))
    )

    pr.on('paymentmethod', async ev => {
      try{
        /* 1) create SetupIntent on server */
        const { data } = await axios.post(
          'https://payme-backend-713827320947.asia-northeast1.run.app/api/payments/setup-intent',
          { paymentMethodType:'wallet' },
          { headers:{ Authorization:`Bearer ${token}` }})
        const { clientSecret } = data
        if(!clientSecret){ ev.complete('fail'); setErrorMsg(t.errGen); return }

        /* 2) confirm with provided wallet PM */
        const confirmRes = await stripe.confirmCardSetup(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions:false }
        )
        if(confirmRes.error){
          ev.complete('fail')
          setErrorMsg(confirmRes.error.message); return
        }

        /* 3) Validate PM type – must be card for ¥ off-session */
        const pmObj = await stripe.paymentMethods.retrieve(
          confirmRes.setupIntent.payment_method
        )
        if(pmObj.type !== 'card'){
          ev.complete('fail')
          setErrorMsg(t.walletBad); return
        }

        ev.complete('success')

        /* 4) create schedule with PM */
        await handleCreateOrUpdateSchedule(
          confirmRes.setupIntent.payment_method,
          'wallet'
        )
      }catch(err){
        console.error('PaymentRequest error:',err)
        ev.complete('fail'); setErrorMsg(err.message||t.errGen)
      }
    })

    paymentRequestRef.current = pr
    setPaymentRequest(pr)
  },[
    stripe,
    form.paymentMethodType,
    form.amount,
    form.toUserEmail,
    token,
    t.errGen,
    t.walletBad
  ])

  /* ───────────────────────────────
     Handlers
  ─────────────────────────────── */
  const handleChange = e => {
    const { name,value } = e.target
    if(name==='amount'){
      const n=parseInt(value.replace(/\D/g,''),10)
      if(!isNaN(n)&&n>0&&n<50) setAmountWarning(t.errSmall)
      else setAmountWarning('')
    }
    setForm({...form,[name]:value})
  }

  const handleRecipientEmailChange = e => {
    const v=e.target.value
    setForm({...form,toUserEmail:v})
    setEmailSearchTerm(v)
    setShowSuggestions(v.length>1)
  }
  const handleSelectSuggestion = it => {
    if(it.id==='inviteFriend'){
      setShowSuggestions(false)
      openInviteModal&&openInviteModal()
      return
    }
    setForm({...form,toUserEmail:it.email})
    setShowSuggestions(false)
  }

  const handlePaymentMethodChange = e => {
    setForm({...form,paymentMethodType:e.target.value})
    setBankInstructions(null); setShowBankModal(false)
    setPaymentRequest(null); setCanUsePaymentRequest(false)
  }

  /* ───────────────────────────────
     Core Save logic (card / bank / edit)
  ─────────────────────────────── */
  const handleSubmit = async e => {
    e.preventDefault()
    if(saving||form.paymentMethodType==='wallet') return
    setSaving(true)

    const amt=parseInt(form.amount.replace(/\D/g,''),10)
    if(!amt||amt<50){ setSaving(false); setErrorMsg(t.errSmall); return }

    try{
      let paymentMethodId=null

      /* 1) card */
      if(form.paymentMethodType==='cc'||form.paymentMethodType==='jcb'){
        const { data } = await axios.post(
          'https://payme-backend-713827320947.asia-northeast1.run.app/api/payments/setup-intent',
          { paymentMethodType:form.paymentMethodType },
          { headers:{ Authorization:`Bearer ${token}` }})
        const { clientSecret } = data
        if(!clientSecret){ throw new Error('No clientSecret') }

        const card=elements.getElement(CardElement)
        const { error,setupIntent } = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method:{
              card,
              billing_details:{ name:user.name, email:user.email },
            },
          })
        if(error){ throw error }
        paymentMethodId = setupIntent.payment_method
      }

      /* 2) bank_transfer – PM id left null */
      if(form.paymentMethodType==='bank_transfer'){
        paymentMethodId=null
      }

      /* 3) create or update schedule */
      await handleCreateOrUpdateSchedule(
        paymentMethodId,
        form.paymentMethodType
      )
    }catch(err){
      console.error('Schedule save error:',err)
      setErrorMsg(err.message||t.errGen)
    }
    setSaving(false)
  }

  /* helper shared by card + wallet flows */
  const handleCreateOrUpdateSchedule = async (paymentMethodId, pmType) => {
    const amt=parseInt(form.amount.replace(/\D/g,''),10)

    if(id){
      await axios.put(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/schedule/${id}`,
        {
          amount:amt, freq:form.freq, start_date:form.startDate,
          payment_method_id:paymentMethodId,
          payment_method_type:pmType,
        },
        { headers:{ Authorization:`Bearer ${token}` }})
    }else{
      const { data:users } = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/users/search?query=${encodeURIComponent(form.toUserEmail)}`,
        { headers:{ Authorization:`Bearer ${token}` }})
      const toUser=users.find(u=>u.email===form.toUserEmail)
      if(!toUser){ throw new Error('Recipient not found') }

      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/schedule/create',
        {
          toUserId:toUser.id, amount:amt, freq:form.freq,
          startDate:form.startDate,
          paymentMethodId, paymentMethodType:pmType,
        },
        { headers:{ Authorization:`Bearer ${token}` }})
      if(res.data.bankInstructions){
        setBankInstructions(res.data.bankInstructions)
        setShowBankModal(true)
      }
    }
    setMsg(t.success)
    setTimeout(()=>nav('/scheduled-payments'),900)
  }

  /* ───────────────────────────────
     JSX
  ─────────────────────────────── */
  if(loading) return <p className="m-4">Loading…</p>

  const prKey=`${form.toUserEmail}-${form.amount}`

  return (
    <div className="max-w-screen-md mx-auto px-2 py-4 space-y-4">
      {/* ───────────── Card ───────────── */}
      <div className="card bg-white border border-gray-200 rounded shadow-sm text-sm">
        {/* Header */}
        <div className="card-header border-b border-dashed p-4">
          <h5 className="text-base md:text-lg font-semibold text-gray-700">
            {id ? t.titleEdit : t.titleNew}
          </h5>
        </div>

        {/* Body */}
        <div className="card-body p-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Recipient (create only) */}
            {!id && (
              <div className="relative">
                <label className="block mb-1 text-gray-700 font-medium">
                  {t.to}
                </label>
                <input
                  type="email"
                  name="toUserEmail"
                  value={form.toUserEmail}
                  onChange={handleRecipientEmailChange}
                  required
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
                />
                {showSuggestions && suggestions.length>0 && (
                  <div className="absolute z-50 bg-white border border-gray-200 rounded mt-1 w-full shadow text-xs md:text-sm">
                    {suggestions.map(it=>(
                      <div
                        key={it.id||it.email}
                        className="px-2 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={()=>handleSelectSuggestion(it)}
                      >
                        {formatSuggestion(it)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Amount */}
            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.amt}
              </label>
              {amountWarning && (
                <p className="text-red-600 text-xs md:text-sm mb-1">
                  {amountWarning}
                </p>
              )}
              <input
                type="text"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                required
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.freq}
              </label>
              <select
                name="freq"
                value={form.freq}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 bg-white focus:outline-none focus:ring focus:ring-blue-500"
              >
                <option value="weekly">{t.weekly}</option>
                <option value="monthly">{t.monthly}</option>
                <option value="yearly">{t.yearly}</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.start}
              </label>
              <input
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
                required
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.pm}
              </label>
              <select
                name="paymentMethodType"
                value={form.paymentMethodType}
                onChange={handlePaymentMethodChange}
                className="w-full border rounded px-3 py-2 bg-white focus:outline-none focus:ring focus:ring-blue-500"
              >
                <option value="wallet">{t.wallet}</option>
                <option value="cc">{t.cc}</option>
                <option value="jcb">{t.jcb}</option>
                <option value="bank_transfer">{t.bank}</option>
              </select>
            </div>

            {/* Apple / Google Pay button */}
            {form.paymentMethodType==='wallet' && paymentRequest && canUsePaymentRequest && (
              <PaymentRequestButtonElement
                key={prKey}
                options={{
                  paymentRequest,
                  style:{ paymentRequestButton:{ height:'44px' } },
                  enableLink:false,
                }}
              />
            )}
            {form.paymentMethodType==='wallet' && paymentRequest && !canUsePaymentRequest && (
              <p className="text-xs md:text-sm text-gray-500">{t.noPaySupport}</p>
            )}

            {/* Card Element */}
            {(form.paymentMethodType==='cc'||form.paymentMethodType==='jcb') && (
              <div>
                <label className="block mb-1 text-gray-700 font-medium">
                  {t.enterCardDetails}
                </label>
                <div className="border rounded px-2 py-3">
                  <CardElement
                    options={{
                      style:{
                        base:{ fontSize:'16px',color:'#424770','::placeholder':{color:'#aab7c4'} },
                      },
                      hidePostalCode:true,
                    }}
                  />
                </div>
              </div>
            )}

            {msg && (
              <p className="text-center text-green-700 text-sm">{msg}</p>
            )}

            {/* Submit */}
            {form.paymentMethodType!=='wallet' && (
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={()=>nav(-1)}
                  className="px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  {saving ? t.sending : t.save}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Bank-transfer Modal */}
      {showBankModal&&bankInstructions&&(
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2">
          <div className="bg-white w-full max-w-md rounded shadow p-4 md:p-6 relative text-sm">
            <button
              onClick={()=>setShowBankModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            >
              &times;
            </button>

            <h4 className="text-base md:text-xl font-semibold mb-4">
              {t.bankTitle}
            </h4>
            <p className="mb-4">{t.furikomiDetails}</p>

            {bankInstructions.financial_addresses?.length>0 ? (
              <div className="space-y-3 text-xs md:text-sm">
                {bankInstructions.financial_addresses.map((addr,i)=>{
                  const d=addr.zengin||addr
                  return (
                    <div key={i} className="border rounded p-3 bg-gray-50">
                      <div><strong>Bank Name:</strong> {d.bank_name||'-'}</div>
                      <div><strong>Branch:</strong> {d.branch_name||'-'}</div>
                      <div><strong>Account Type:</strong> {d.account_type||d.account_holder_type||'-'}</div>
                      <div><strong>Account Number:</strong> {d.account_number||'-'}</div>
                    </div>
                  )
                })}
              </div>
            ):<p>{t.errGen}</p>}

            <div className="text-right mt-6">
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={()=>setShowBankModal(false)}
              >
                {t.okay}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMsg && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2">
          <div className="bg-white w-full max-w-md rounded shadow p-4 md:p-6 relative text-sm">
            <button
              onClick={()=>setErrorMsg(null)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            >
              &times;
            </button>

            <h4 className="text-base md:text-lg font-semibold mb-4 text-red-600">
              {t.errGen}
            </h4>
            <p className="whitespace-pre-wrap break-words max-h-60 overflow-y-auto mb-6">
              {errorMsg}
            </p>
            <div className="text-right">
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={()=>setErrorMsg(null)}
              >
                {t.okay}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SchedulePaymentPage
// ────────────────────────────────────────────────────────────────
// END – SchedulePaymentPage.js  (wallet PM guard + bug-fixed)
// ────────────────────────────────────────────────────────────────
